import React, { useRef, useState, useEffect } from 'react';
import { styles as globalStyles } from '../../../../utils/styles';
import DeltaSwitch from 'Components/GMS/Products/ProductView/DeltaSwitch';
import Slider from 'Pages/GMS/ABTesting/components/Slider';
import { Container } from '../../../../types/canvas.types';
import { LockedIcon, UnlockedIcon } from 'assests/GmsIcons';
import RefreshIcon from 'Components/GMS/Builder/Components/RefreshIcon';

type ContainerConfigType = {
  id: string;
  name: string;
  width: number;
  widthUnit: '%' | 'px';
  height: number | 'Dynamic';
  heightUnit: 'px';
  alignment: 'left' | 'center' | 'right';
  layout: 'vertical' | 'horizontal';
  showTitle: boolean;
  widthLocked?: boolean;
  collapsible?: boolean;
  behaveAsFilter?: boolean;
};

interface ContainerConfigPanelProps {
  id?: string;
  config: ContainerConfigType;
  setConfig: React.Dispatch<React.SetStateAction<ContainerConfigType>>;
  selectedContainerId: string | null;
  activePageId: string | null;
  containers: Record<string, Container[]>;
  setContainers: React.Dispatch<React.SetStateAction<Record<string, Container[]>>>;
  containersCount: number;
  containersInRow: Container[];
  handleWidthInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleWidthChange: (value: number) => void;
  toggleWidthLock: () => void;
  resetRowWidths: () => void;
  isContainerNested: (containerId: string) => boolean;
}

export const ContainerConfigPanel: React.FC<ContainerConfigPanelProps> = ({
  config,
  setConfig,
  selectedContainerId,
  activePageId,
  containers,
  setContainers,
  containersCount,
  containersInRow,
  handleWidthInputChange,
  handleWidthChange,
  toggleWidthLock,
  resetRowWidths,
  isContainerNested
}) => {
  const styles = {
    section: "tw-mb-4 tw-mx-4",
    sectionLabel: "tw-text-[#6D788C] tw-text-sm tw-mb-2",
    widthContainer: "tw-flex tw-items-center tw-gap-4",
    valueInput: "tw-flex tw-items-center tw-border tw-text-[#091E42] tw-border-[#97A0AF] tw-rounded tw-px-2 tw-py-1 tw-min-w-[70px]",
    numericInput: "tw-w-9 tw-outline-none",
    dynamicHeightText: "tw-text-[#091E42] tw-text-sm tw-font-semibold",
    layoutDropdown: "tw-relative",
    select: "tw-w-full tw-appearance-none tw-border tw-border-[#DADBDD] tw-rounded tw-px-3 tw-py-2 tw-pr-8 tw-text-[#172B4D] tw-text-sm tw-bg-white",
    selectIcon: "tw-absolute tw-right-3 tw-top-1/2 tw-transform tw-translate-y-[-50%]",
    checkboxContainer: "tw-flex tw-items-center",
    checkboxInput: `${globalStyles.checkboxInput}`,
    checkboxLabel: "tw-text-[#172B4D] tw-text-sm",
  };

  // Debouncing for width controls
  const widthDebounceRef = useRef<NodeJS.Timeout>();
  const [localWidth, setLocalWidth] = useState<number>(config.width);

  // Debounced handler for width input
  const handleDebouncedWidthInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setLocalWidth(value);

    if (widthDebounceRef.current) {
      clearTimeout(widthDebounceRef.current);
    }

    widthDebounceRef.current = setTimeout(() => {
      handleWidthInputChange(e);
    }, 500);
  };

  // Debounced handler for width slider
  const handleDebouncedWidthSliderChange = (value: number) => {
    setLocalWidth(value);

    if (widthDebounceRef.current) {
      clearTimeout(widthDebounceRef.current);
    }

    widthDebounceRef.current = setTimeout(() => {
      handleWidthChange(value);
    }, 500);
  };

  // Sync local width when config.width changes externally
  useEffect(() => {
    setLocalWidth(config.width);
  }, [config.width]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (widthDebounceRef.current) {
        clearTimeout(widthDebounceRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Width Section */}
      <div className={styles.section}>
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <h3 className={styles.sectionLabel}>Width</h3>

          {/* Width Lock Toggle */}
          <button
            onClick={toggleWidthLock}
            className="tw-p-1 tw-rounded hover:tw-bg-gray-100"
            title={config.widthLocked ? "Unlock width" : "Lock width"}
          >
            {config.widthLocked ? <LockedIcon /> : <UnlockedIcon />}
          </button>
        </div>

        <div className="tw-bg-[#F9FAFB] tw-p-3 tw-rounded-md">
          <div className={styles.widthContainer}>
            <div className={styles.valueInput}>
              <input
                type="text"
                inputMode="numeric"
                value={localWidth}
                onChange={handleDebouncedWidthInputChange}
                className={`${styles.numericInput} ${config.widthLocked ? 'tw-border-blue-400' : ''}`}
              />
              <span className="tw-ml-1">{config.widthUnit}</span>
            </div>
            <Slider
              disable={false}
              value={localWidth}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                handleDebouncedWidthSliderChange(value);
              }}
              max={containersCount === 1 ? 100 : containersCount === 2 ? 75 : 50}
              min={25}
            />
          </div>

          {/* Reset Row Button */}
          <div className="tw-mt-2 tw-flex tw-justify-end">
            <button
              onClick={resetRowWidths}
              className="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 tw-flex tw-items-center"
              title="Reset all widths in this row"
            >
              <span className="tw-mr-1 tw-text-xs tw-w-6 tw-h-6"><RefreshIcon /></span>
              Reset row
            </button>
          </div>
        </div>

        {/* Width Distribution Info */}
        {containersCount > 1 && (
          <div className="tw-mt-2 tw-text-xs tw-text-gray-500">
            <div className="tw-mb-1">Containers in this row:</div>
            <div className="tw-flex tw-flex-wrap tw-gap-1">
              {containersInRow.map(container => (
                <div
                  key={container.id}
                  className={`tw-px-2 tw-py-1 tw-rounded ${
                    container.id === selectedContainerId
                      ? 'tw-bg-blue-100 tw-text-blue-800'
                      : 'tw-bg-gray-100'
                  }`}
                >
                  <span className="tw-mr-1">{container.name}:</span>
                  <span className="tw-font-semibold">{container.configuration.width}</span>
                  {container.configuration.widthLocked && <span className="tw-ml-1">🔒</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Height */}
      <div className={styles.section}>
        <h3 className={styles.sectionLabel}>Height</h3>
        <div className="tw-rounded-md">
          <div className={styles.dynamicHeightText}>Dynamic</div>
        </div>
      </div>

      {/* Container Alignment */}
      <div className={styles.section}>
        <h3 className={styles.sectionLabel}>Container Alignment</h3>
        <div className="tw-rounded-md tw-w-[85%]">
          <DeltaSwitch
            id="alignment-status-switch"
            labels={['Left', 'Centre', 'Right']}
            defaultSelectedIndex={
              config.alignment === 'left' ? 0 :
              config.alignment === 'center' ? 1 :
              config.alignment === 'right' ? 2 : 0
            }
            onSelectionChange={(value) => {
              let alignment: 'left' | 'center' | 'right';

              if (value === 'Left') alignment = 'left';
              else if (value === 'Centre') alignment = 'center';
              else if (value === 'Right') alignment = 'right';
              else alignment = 'left'; // Default fallback

              setConfig(prev => ({ ...prev, alignment }));

              if (selectedContainerId && activePageId) {
                setContainers(prevContainers => {
                  if (!prevContainers[activePageId]) return prevContainers;

                  const clonedContainers = [...prevContainers[activePageId]];

                  const isNested = isContainerNested(selectedContainerId);

                  if (isNested) {
                    const updateNestedContainerAlignment = (containers: Container[]): boolean => {
                      for (let i = 0; i < containers.length; i++) {
                        const container = containers[i];

                        if (container.id === selectedContainerId) {
                          container.configuration.alignment = alignment;
                          return true;
                        }

                        if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                          const targetIndex = container.content.nestedContainers.findIndex(
                            c => c.id === selectedContainerId
                          );

                          if (targetIndex >= 0) {
                            container.content.nestedContainers[targetIndex].configuration.alignment = alignment;
                            return true;
                          }

                          const found = updateNestedContainerAlignment(container.content.nestedContainers);
                          if (found) return true;
                        }
                      }

                      return false;
                    };

                    updateNestedContainerAlignment(clonedContainers);
                  } else {
                    clonedContainers.forEach(container => {
                      if (container.id === selectedContainerId) {
                        container.configuration.alignment = alignment;
                      }
                    });
                  }

                  return {
                    ...prevContainers,
                    [activePageId]: clonedContainers
                  };
                });
              }
            }}
          />
        </div>
      </div>

      {/* Layout Direction */}
      <div className={styles.section}>
        <h3 className={styles.sectionLabel}>Layout</h3>
        <div className="tw-rounded-md">
          <div className={styles.layoutDropdown}>
            <select
              value={config.layout}
              onChange={(e) => {
                const layout = e.target.value as 'vertical' | 'horizontal';
                setConfig(prev => ({ ...prev, layout }));

                if (selectedContainerId && activePageId) {
                  setContainers(prevContainers => {
                    if (!prevContainers[activePageId]) return prevContainers;

                    const clonedContainers = [...prevContainers[activePageId]];

                    const isNested = isContainerNested(selectedContainerId);

                    if (isNested) {
                      const updateNestedContainerLayout = (containers: Container[]): boolean => {
                        for (let i = 0; i < containers.length; i++) {
                          const container = containers[i];

                          if (container.id === selectedContainerId) {
                            container.configuration.layout = layout;
                            return true;
                          }

                          if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                            const targetIndex = container.content.nestedContainers.findIndex(
                              c => c.id === selectedContainerId
                            );

                            if (targetIndex >= 0) {
                              container.content.nestedContainers[targetIndex].configuration.layout = layout;
                              return true;
                            }

                            const found = updateNestedContainerLayout(container.content.nestedContainers);
                            if (found) return true;
                          }
                        }

                        return false;
                      };

                      updateNestedContainerLayout(clonedContainers);
                    } else {
                      clonedContainers.forEach(container => {
                        if (container.id === selectedContainerId) {
                          container.configuration.layout = layout;
                        }
                      });
                    }

                    return {
                      ...prevContainers,
                      [activePageId]: clonedContainers
                    };
                  });
                }
              }}
              className={styles.select}
            >
              <option value="vertical">Vertical</option>
              <option value="horizontal">Horizontal</option>
            </select>
            <div className={styles.selectIcon}>
              <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L7 7L13 1" stroke="#172B4D" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Show Container Title */}
      <div className={styles.section}>
        <div className=" tw-rounded-md">
          <label className={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={config.showTitle}
              onChange={(e) => {
                const showTitle = e.target.checked;
                setConfig(prev => ({ ...prev, showTitle }));

                if (selectedContainerId && activePageId) {
                  setContainers(prevContainers => {
                    if (!prevContainers[activePageId]) return prevContainers;

                    const clonedContainers = [...prevContainers[activePageId]];

                    const isNested = isContainerNested(selectedContainerId);

                    if (isNested) {
                      const updateNestedContainerShowTitle = (containers: Container[]): boolean => {
                        for (let i = 0; i < containers.length; i++) {
                          const container = containers[i];

                          if (container.id === selectedContainerId) {
                            container.configuration.showTitle = showTitle;
                            return true;
                          }

                          if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                            const targetIndex = container.content.nestedContainers.findIndex(
                              c => c.id === selectedContainerId
                            );

                            if (targetIndex >= 0) {
                              container.content.nestedContainers[targetIndex].configuration.showTitle = showTitle;
                              return true;
                            }

                            const found = updateNestedContainerShowTitle(container.content.nestedContainers);
                            if (found) return true;
                          }
                        }

                        return false;
                      };

                      updateNestedContainerShowTitle(clonedContainers);
                    } else {
                      clonedContainers.forEach(container => {
                        if (container.id === selectedContainerId) {
                          container.configuration.showTitle = showTitle;
                        }
                      });
                    }

                    return {
                      ...prevContainers,
                      [activePageId]: clonedContainers
                    };
                  });
                }
              }}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxLabel}>{`Show 'Container Title'`}</span>
          </label>
        </div>
      </div>

      {/* Collapsible */}
      <div className={styles.section}>
        <div className=" tw-rounded-md">
          <label className={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={config.collapsible || false}
              onChange={(e) => {
                const collapsible = e.target.checked;
                setConfig(prev => ({ ...prev, collapsible }));

                if (selectedContainerId && activePageId) {
                  setContainers(prevContainers => {
                    if (!prevContainers[activePageId]) return prevContainers;

                    const clonedContainers = [...prevContainers[activePageId]];

                    const isNested = isContainerNested(selectedContainerId);

                    if (isNested) {
                      const updateNestedContainerCollapsible = (containers: Container[]): boolean => {
                        for (let i = 0; i < containers.length; i++) {
                          const container = containers[i];

                          if (container.id === selectedContainerId) {
                            container.configuration.collapsible = collapsible;
                            return true;
                          }

                          if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                            const targetIndex = container.content.nestedContainers.findIndex(
                              c => c.id === selectedContainerId
                            );

                            if (targetIndex >= 0) {
                              container.content.nestedContainers[targetIndex].configuration.collapsible = collapsible;
                              return true;
                            }

                            const found = updateNestedContainerCollapsible(container.content.nestedContainers);
                            if (found) return true;
                          }
                        }

                        return false;
                      };

                      updateNestedContainerCollapsible(clonedContainers);
                    } else {
                      clonedContainers.forEach(container => {
                        if (container.id === selectedContainerId) {
                          container.configuration.collapsible = collapsible;
                        }
                      });
                    }

                    return {
                      ...prevContainers,
                      [activePageId]: clonedContainers
                    };
                  });
                }
              }}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxLabel}>Collapsible</span>
          </label>
        </div>
      </div>

      {/* Behave as Filter */}
      <div className={styles.section}>
        <div className=" tw-rounded-md">
          <label className={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={config.behaveAsFilter || false}
              onChange={(e) => {
                const behaveAsFilter = e.target.checked;
                setConfig(prev => ({ ...prev, behaveAsFilter }));

                if (selectedContainerId && activePageId) {
                  setContainers(prevContainers => {
                    if (!prevContainers[activePageId]) return prevContainers;

                    const clonedContainers = [...prevContainers[activePageId]];

                    const isNested = isContainerNested(selectedContainerId);

                    if (isNested) {
                      const updateNestedContainerBehaveAsFilter = (containers: Container[]): boolean => {
                        for (let i = 0; i < containers.length; i++) {
                          const container = containers[i];

                          if (container.id === selectedContainerId) {
                            container.configuration.behaveAsFilter = behaveAsFilter;
                            return true;
                          }

                          if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                            const targetIndex = container.content.nestedContainers.findIndex(
                              c => c.id === selectedContainerId
                            );

                            if (targetIndex >= 0) {
                              container.content.nestedContainers[targetIndex].configuration.behaveAsFilter = behaveAsFilter;
                              return true;
                            }

                            const found = updateNestedContainerBehaveAsFilter(container.content.nestedContainers);
                            if (found) return true;
                          }
                        }

                        return false;
                      };

                      updateNestedContainerBehaveAsFilter(clonedContainers);
                    } else {
                      clonedContainers.forEach(container => {
                        if (container.id === selectedContainerId) {
                          container.configuration.behaveAsFilter = behaveAsFilter;
                        }
                      });
                    }

                    return {
                      ...prevContainers,
                      [activePageId]: clonedContainers
                    };
                  });
                }
              }}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxLabel}>Behave as a Filter</span>
          </label>
        </div>
      </div>
    </>
  );
};
